import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  CheckCircle2,
  ChevronLeft,
  Crown,
  Loader2,
  Pause,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  UserMinus,
  X,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { useSession } from "@/lib/auth/session";
import { useHubConnection } from "@/lib/hub/useHubConnection";
import { useToastMutation } from "@/lib/mutations";
import {
  notesApi,
  notesQueries,
  ShareRole,
  type NoteDto,
  type NoteShareDto,
  type NoteUpdatedEvent,
} from "@/lib/notes/api";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Field } from "@/components/form/Field";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { formatRelative } from "@/lib/format";
import { TipTapEditor, type TipTapEditorHandle } from "@/components/editor/TipTapEditor";
import { DrawingOverlay } from "@/components/editor/DrawingOverlay";
import { drawingsApi, type NoteDrawingSavedDto } from "@/lib/notes/api";

export const Route = createFileRoute("/o/$slug/notes/$noteId")({
  component: NoteDetailPage,
});

const editorSchema = z.object({
  title: z.string().max(256, "Maximal 256 Zeichen.").default(""),
  body: z.string().max(200_000, "Maximal 200.000 Zeichen.").default(""),
});
type EditorValues = z.infer<typeof editorSchema>;

function NoteDetailPage() {
  const org = useOrg();
  const { noteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();

  const note = useQuery(notesQueries.detail(org.slug, noteId));

  return (
    <PageContainer>
      <div className="mb-4">
        <Link
          to="/o/$slug/notes"
          params={{ slug: org.slug }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Notizen
        </Link>
      </div>

      {note.isLoading && <DetailSkeleton />}
      {note.error && <DetailError error={note.error} slug={org.slug} />}

      {note.data && (
        <NoteEditor
          // Re-mount when switching notes so autosave timers / conflict flags
          // don't leak across notes. Cheap insurance even if TanStack Router
          // already re-mounts on param change.
          key={note.data.id}
          slug={org.slug}
          note={note.data}
          isOwner={note.data.ownerId === session.userId}
          onDeleted={() => {
            queryClient.invalidateQueries({ queryKey: notesQueries.list(org.slug).queryKey });
            void navigate({ to: "/o/$slug/notes", params: { slug: org.slug } });
          }}
          onRemoteUpdate={(payload) => {
            queryClient.setQueryData<NoteDto>(
              notesQueries.detail(org.slug, noteId).queryKey,
              (prev) =>
                prev
                  ? { ...prev, title: payload.title, body: payload.body, updatedAt: payload.updatedAt }
                  : prev,
            );
          }}
        />
      )}
    </PageContainer>
  );
}

interface NoteEditorProps {
  slug: string;
  note: NoteDto;
  isOwner: boolean;
  onDeleted: () => void;
  onRemoteUpdate: (payload: NoteUpdatedEvent) => void;
}

function NoteEditor({ slug, note, isOwner, onDeleted, onRemoteUpdate }: NoteEditorProps) {
  const queryClient = useQueryClient();
  // What the server thinks the latest version is. Drives the 409 reconcile —
  // we send `knownUpdatedAt` so the server can reject stale writes.
  const [serverUpdatedAt, setServerUpdatedAt] = useState(note.updatedAt);
  // True when we received a remote NoteUpdated while the buffer was dirty —
  // suspends auto-save so we don't silently clobber the foreign edit.
  const [conflictPending, setConflictPending] = useState(false);

  const form = useForm<EditorValues>({
    resolver: zodResolver(editorSchema),
    defaultValues: { title: note.title, body: note.body },
    mode: "onBlur",
  });

  // Re-baseline the form when the server-side note changes (initial load,
  // post-save, or remote NoteUpdated while buffer was clean).
  useEffect(() => {
    setServerUpdatedAt(note.updatedAt);
    if (!form.formState.isDirty) {
      form.reset({ title: note.title, body: note.body });
    }
  }, [note.id, note.updatedAt, note.title, note.body, form]);

  const save = useToastMutation({
    mutationFn: (values: EditorValues) =>
      notesApi.update(slug, note.id, {
        title: values.title,
        body: values.body,
        knownUpdatedAt: serverUpdatedAt,
      }),
    // Auto-save fires every ~1.2s of idle time and manual save is already
    // reflected in the SaveButton's label/icon flip — a toast on every save
    // would be noise. Errors (especially 409 / ApiError) still surface.
    toasts: { success: false },
    onSuccess: (updated) => {
      setServerUpdatedAt(updated.updatedAt);
      setConflictPending(false);
      form.reset({ title: updated.title, body: updated.body });
      queryClient.setQueryData<NoteDto>(notesQueries.detail(slug, note.id).queryKey, updated);
      queryClient.invalidateQueries({ queryKey: notesQueries.list(slug).queryKey });
    },
    onError: async (err) => {
      // 409: the server returned the live state. Refetch and arm conflict mode
      // so the user has to either explicitly merge or revert before saving again.
      if (err instanceof ApiError && err.status === 409) {
        setConflictPending(true);
        await queryClient.invalidateQueries({
          queryKey: notesQueries.detail(slug, note.id).queryKey,
        });
      }
    },
  });

  const remove = useToastMutation({
    mutationFn: () => notesApi.remove(slug, note.id),
    toasts: { success: "Notiz gelöscht." },
    onSuccess: onDeleted,
  });

  // SignalR: subscribe to this note's updates while mounted.
  useHubConnection({
    path: "/hubs/notes",
    onConnected: (conn) => conn.invoke("SubscribeAsync", note.id),
    on: {
      NoteUpdated: (...args: unknown[]) => {
        const payload = args[0] as NoteUpdatedEvent | undefined;
        if (!payload || payload.id !== note.id) return;
        // Always advance the server-version stamp so subsequent saves don't 409.
        setServerUpdatedAt(payload.updatedAt);
        if (form.formState.isDirty) {
          // Buffer is dirty — surface the conflict instead of overwriting.
          setConflictPending(true);
        } else {
          form.reset({ title: payload.title, body: payload.body });
        }
        onRemoteUpdate(payload);
      },
      NoteDeleted: (...args: unknown[]) => {
        const id = args[0] as string | undefined;
        if (id === note.id) onDeleted();
      },
    },
  });

  const onSubmit = form.handleSubmit((values) => save.mutate(values));
  const dirty = form.formState.isDirty;

  // ── Idle autosave ───────────────────────────────────────────────────
  // Fires 1.2 s after the last keystroke when the buffer is dirty and not
  // currently saving / conflict-suspended. The watcher subscribes to ALL
  // form fields via form.watch() — every keystroke re-runs the effect, which
  // clears the previous timer and schedules a new one. Stable across renders
  // (RHF's watch returns referentially-stable strings).
  const watchedTitle = form.watch("title");
  const watchedBody = form.watch("body");
  useEffect(() => {
    if (!dirty || conflictPending || save.isPending) return;
    const timer = setTimeout(() => {
      void onSubmit();
    }, 1200);
    return () => clearTimeout(timer);
    // onSubmit is recreated each render via form.handleSubmit; that's fine —
    // we want the latest closure (with up-to-date serverUpdatedAt etc.).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedTitle, watchedBody, dirty, conflictPending, save.isPending]);

  // Save status — drives the indicator next to the Speichern button.
  const saveStatus: SaveStatusKind = save.isPending
    ? "saving"
    : conflictPending
      ? "suspended"
      : dirty
        ? "dirty"
        : "saved";

  // ── Rich editor + drawings integration ──────────────────────────────
  const editorRef = useRef<TipTapEditorHandle>(null);
  const [drawingDialog, setDrawingDialog] = useState<
    { open: true; drawingId: string | null } | { open: false }
  >({ open: false });

  const bodyValue = form.watch("body");
  const onBodyChange = (html: string) => {
    // mark dirty so Speichern lights up + 409-reconcile path knows we changed it
    form.setValue("body", html, { shouldDirty: true });
  };

  const loadDrawingDataUrl = (drawingId: string) =>
    drawingsApi.fetchPngDataUrl(slug, note.id, drawingId).catch((err) => {
      console.warn("Drawing fetch failed", drawingId, err);
      return null;
    });

  const onDrawingSaved = (saved: NoteDrawingSavedDto) => {
    // Server bumped the note's UpdatedAt via Touch(); advance our baseline so
    // the next body PUT doesn't 409 on its own write.
    setServerUpdatedAt(saved.noteUpdatedAt);
    editorRef.current?.insertDrawing({
      drawingId: saved.id,
      format: "excalidraw@v1",
      dataSrc: drawingsApi.pngUrl(slug, note.id, saved.id, new Date(saved.updatedAt).getTime()),
      width: saved.width,
      height: saved.height,
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          <div className="flex items-end justify-between gap-2">
            <Field<EditorValues> name="title" label="Titel" className="flex-1">
              <Input placeholder="Titel der Notiz" />
            </Field>
            <div className="flex items-center gap-2">
              <SaveButton status={saveStatus} />
              {isOwner && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm("Notiz wirklich löschen?")) remove.mutate(undefined);
                  }}
                  disabled={remove.isPending}
                  title="Löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {conflictPending && (
            <ConflictBanner
              onRevert={() => {
                form.reset({ title: note.title, body: note.body });
                setConflictPending(false);
              }}
              onOverwrite={() => {
                // Save with the freshly-advanced serverUpdatedAt — accepts the
                // collision and overwrites with the local buffer.
                setConflictPending(false);
                void onSubmit();
              }}
            />
          )}

          <div className="grid gap-1.5">
            <Label>Inhalt</Label>
            <TipTapEditor
              ref={editorRef}
              value={bodyValue}
              onChange={onBodyChange}
              loadDrawingDataUrl={loadDrawingDataUrl}
              onDrawingClick={(drawingId) => setDrawingDialog({ open: true, drawingId })}
              onInsertDrawing={() => setDrawingDialog({ open: true, drawingId: null })}
              onConfirmDrawingRemoval={(drawingIds) => {
                // Deletion can't be undone server-side (body-PUT triggers an
                // orphan sweep that deletes the row + PNG), so we ask before
                // replaying. The editor has already restored the drawings so
                // a "Cancel" leaves them in place.
                const n = drawingIds.length;
                const message =
                  n === 1
                    ? "Zeichnung wirklich entfernen? Sie kann später nicht wiederhergestellt werden."
                    : `${n} Zeichnungen wirklich entfernen? Sie können später nicht wiederhergestellt werden.`;
                if (window.confirm(message)) {
                  editorRef.current?.confirmRemoveDrawings(drawingIds);
                }
              }}
            />
          </div>

        </div>

        <SidePanel slug={slug} note={note} isOwner={isOwner} lastSavedAt={serverUpdatedAt} />
      </form>

      <DrawingOverlay
        open={drawingDialog.open}
        onClose={() => setDrawingDialog({ open: false })}
        slug={slug}
        noteId={note.id}
        drawingId={drawingDialog.open ? drawingDialog.drawingId : null}
        onSaved={onDrawingSaved}
      />
    </FormProvider>
  );
}

type SaveStatusKind = "saving" | "suspended" | "dirty" | "saved";

/**
 * Submit button whose label, icon, variant + disabled state all reflect the
 * current save status. Replaces a separate "saved/dirty/…" indicator next to
 * the button — keeps the affordance and the status feedback in the same
 * place.
 *
 *   saved     → outline, "Gespeichert", disabled  (no edits → nothing to do)
 *   dirty     → primary, "Speichern", enabled
 *   saving    → primary, "Wird gespeichert …", spinner, disabled
 *   suspended → outline + amber, "Pausiert", disabled  (user must Revert/
 *               Überschreiben via the conflict banner first)
 */
function SaveButton({ status }: { status: SaveStatusKind }) {
  switch (status) {
    case "saving":
      return (
        <Button type="submit" disabled>
          <Loader2 className="h-4 w-4 animate-spin" />
          Wird gespeichert …
        </Button>
      );
    case "suspended":
      return (
        <Button
          type="button"
          variant="outline"
          disabled
          className="text-amber-700"
          title="Auto-Speichern pausiert wegen Remote-Konflikt — bitte oben Verwerfen oder Überschreiben wählen."
        >
          <Pause className="h-4 w-4" />
          Pausiert
        </Button>
      );
    case "dirty":
      return (
        <Button type="submit">
          <Save className="h-4 w-4" />
          Speichern
        </Button>
      );
    case "saved":
      return (
        <Button type="button" variant="outline" disabled className="text-emerald-600">
          <CheckCircle2 className="h-4 w-4" />
          Gespeichert
        </Button>
      );
  }
}

function ConflictBanner({
  onRevert,
  onOverwrite,
}: {
  onRevert: () => void;
  onOverwrite: () => void;
}) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <p className="font-medium">Konflikt mit Remote-Bearbeitung.</p>
      <p className="mt-1 text-xs">
        Die Notiz wurde zwischenzeitlich extern geändert. Verwirf deine Änderungen oder überschreibe
        bewusst.
      </p>
      <div className="mt-2 flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onRevert}>
          <RotateCcw className="h-3.5 w-3.5" />
          Verwerfen
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onOverwrite}>
          Überschreiben
        </Button>
      </div>
    </div>
  );
}

// ── Side panel: shares, owner badge, metadata ───────────────────────────────

function SidePanel({
  slug,
  note,
  isOwner,
  lastSavedAt,
}: {
  slug: string;
  note: NoteDto;
  isOwner: boolean;
  /** Authoritative server-side UpdatedAt — same value the next PUT sends as KnownUpdatedAt. */
  lastSavedAt: string;
}) {
  const session = useSession();
  return (
    <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-lg border bg-card p-4 text-sm">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Crown className="h-3.5 w-3.5" />
          Eigentümer:in
        </div>
        <div className="mt-1 truncate font-medium">
          {note.ownerId === session.userId ? "Du" : note.ownerId}
        </div>
        <Separator className="my-3" />
        <dl className="space-y-1 text-xs text-muted-foreground">
          <div>Erstellt {formatRelative(note.createdAt) ?? "—"}</div>
          <div>Zuletzt geändert {formatRelative(lastSavedAt) ?? "—"}</div>
        </dl>
      </div>

      <SharesPanel slug={slug} noteId={note.id} canManage={isOwner} />
    </aside>
  );
}

function SharesPanel({
  slug,
  noteId,
  canManage,
}: {
  slug: string;
  noteId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const session = useSession();
  const shares = useQuery(notesQueries.shares(slug, noteId));
  const candidates = useQuery({ ...notesQueries.invitationCandidates(slug), enabled: canManage });

  const [picked, setPicked] = useState<string>("");
  const [pickedRole, setPickedRole] = useState<ShareRole>(ShareRole.Editor);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: notesQueries.shares(slug, noteId).queryKey });

  const add = useToastMutation({
    mutationFn: () => notesApi.addShare(slug, noteId, { userId: picked, role: pickedRole }),
    toasts: { success: "Geteilt." },
    onSuccess: () => {
      setPicked("");
      invalidate();
    },
  });

  const updateRole = useToastMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ShareRole }) =>
      notesApi.updateShare(slug, noteId, userId, role),
    toasts: { success: "Rolle geändert." },
    onSuccess: invalidate,
  });

  const removeShare = useToastMutation({
    mutationFn: ({ userId }: { userId: string }) => notesApi.removeShare(slug, noteId, userId),
    toasts: { success: "Zugriff entfernt." },
    onSuccess: invalidate,
  });

  const eligible =
    candidates.data?.filter(
      (c) => !shares.data?.some((s) => s.userId === c.userId) && c.userId !== session.userId,
    ) ?? [];

  return (
    <div className="rounded-lg border bg-card p-4 text-sm">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Geteilt mit</h3>

      {shares.isLoading && <Skeleton className="mt-2 h-12" />}
      {shares.error && (
        <p className="mt-2 text-xs text-destructive">
          Konnte Freigaben nicht laden: {(shares.error as Error).message}
        </p>
      )}

      {shares.data && shares.data.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Niemand außer dir.</p>
      )}

      {shares.data && shares.data.length > 0 && (
        <ul className="mt-2 space-y-2">
          {shares.data.map((s) => (
            <ShareRow
              key={s.userId}
              share={s}
              canManage={canManage}
              isMe={s.userId === session.userId}
              onChangeRole={(role) => updateRole.mutate({ userId: s.userId, role })}
              onRemove={() => removeShare.mutate({ userId: s.userId })}
              busy={updateRole.isPending || removeShare.isPending}
            />
          ))}
        </ul>
      )}

      {canManage && (
        <>
          <Separator className="my-3" />
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Einladen</Label>
          <div className="mt-2 grid gap-2">
            <select
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="">Kollege:in auswählen…</option>
              {eligible.map((c) => (
                <option key={c.userId} value={c.userId}>
                  {c.displayName ?? c.userId}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <select
                value={pickedRole}
                onChange={(e) => setPickedRole(Number(e.target.value) as ShareRole)}
                className="h-9 flex-1 rounded-md border border-input bg-transparent px-2 text-sm"
              >
                <option value={ShareRole.Editor}>Editor</option>
                <option value={ShareRole.Viewer}>Viewer</option>
              </select>
              <Button
                type="button"
                size="sm"
                disabled={!picked || add.isPending}
                onClick={() => add.mutate(undefined)}
              >
                <Plus className="h-3.5 w-3.5" />
                Hinzufügen
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ShareRow({
  share,
  canManage,
  isMe,
  onChangeRole,
  onRemove,
  busy,
}: {
  share: NoteShareDto;
  canManage: boolean;
  isMe: boolean;
  onChangeRole: (role: ShareRole) => void;
  onRemove: () => void;
  busy: boolean;
}) {
  return (
    <li className="flex items-center gap-2 rounded-md border bg-background p-2 text-xs">
      <div className="min-w-0 flex-1 truncate">
        {share.displayName ?? share.userId}
        {isMe && <span className="ml-1 text-muted-foreground">(du)</span>}
      </div>
      {canManage && !isMe ? (
        <select
          value={share.role}
          onChange={(e) => onChangeRole(Number(e.target.value) as ShareRole)}
          disabled={busy}
          className="h-7 rounded border border-input bg-transparent px-1 text-xs"
        >
          <option value={ShareRole.Editor}>Editor</option>
          <option value={ShareRole.Viewer}>Viewer</option>
        </select>
      ) : (
        <span className="text-muted-foreground">
          {share.role === ShareRole.Editor ? "Editor" : "Viewer"}
        </span>
      )}
      {(canManage || isMe) && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          title={isMe ? "Mich entfernen" : "Entfernen"}
        >
          {isMe ? <X className="h-3.5 w-3.5" /> : <UserMinus className="h-3.5 w-3.5" />}
        </button>
      )}
    </li>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <div className="space-y-3">
        <Skeleton className="h-9" />
        <Skeleton className="h-[400px]" />
      </div>
      <Skeleton className="h-32" />
    </div>
  );
}

function DetailError({ error, slug }: { error: Error; slug: string }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
      <p className="text-destructive">
        {status === 404
          ? "Notiz nicht gefunden oder kein Zugriff."
          : `Konnte Notiz nicht laden: ${error.message}`}
      </p>
      <Link
        to="/o/$slug/notes"
        params={{ slug }}
        className="mt-3 inline-block text-xs text-muted-foreground underline hover:text-foreground"
      >
        Zurück zur Liste
      </Link>
    </div>
  );
}
